import React, { useRef, useEffect } from 'react';
import type { Tile } from '@shared/types';

interface TileComponentProps {
  tile: Tile;
  selected?: boolean;
  onClick?: () => void;
  onPointerDown?: (e: React.PointerEvent<HTMLDivElement>, tileId: string) => void;
  disabled?: boolean;
  animate?: boolean;
  isDragging?: boolean;
}

function getDisplayNumber(tile: Tile): string {
  if (tile.isJoker) return '☆';
  if (tile.number === 0) return '☆';
  return String(tile.number);
}

export const TileComponent = React.memo(function TileComponent({
  tile,
  selected = false,
  onClick,
  onPointerDown,
  disabled = false,
  animate = false,
  isDragging = false,
}: TileComponentProps) {
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (animate && divRef.current) {
      divRef.current.classList.add('animate-tile-drop');
      const handler = () => divRef.current?.classList.remove('animate-tile-drop');
      divRef.current.addEventListener('animationend', handler, { once: true });
    }
  }, [animate]);

  const classNames = [
    'tile',
    tile.color,
    tile.isOkey ? 'okey-tile' : '',
    tile.isJoker ? 'fake-joker' : '',
    selected ? 'selected' : '',
    isDragging ? 'dragging' : '',
    disabled ? 'disabled' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    onPointerDown?.(e, tile.id);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (disabled) return;
    e.stopPropagation();
    onClick?.();
  };

  const displayNumber = getDisplayNumber(tile);

  return (
    <div
      ref={divRef}
      className={classNames}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      data-tile-id={tile.id}
      role="button"
      aria-pressed={selected}
      aria-label={`${tile.color} ${displayNumber}`}
    >
      <span className={`tile-number ${tile.isJoker || tile.number === 0 ? 'joker' : tile.color}`}>
        {displayNumber}
      </span>
      {!tile.isJoker && tile.number !== 0 && (
        <span className={`tile-dot ${tile.color}`} />
      )}
    </div>
  );
});

export default TileComponent;
